/**
 * Deterministic step sequencer for the music stems (08 §6, 15 §4.2). Patterns
 * are 16th-note grids; instruments are pure functions `(sr, vel, note, rng)`
 * that return one hit with its natural tail. `renderPattern` renders
 * `bars + 1` bars and folds the overflow bar back onto the loop head so the
 * seam is continuous in both the shipped Ogg and the live-synth fallback.
 * No Math.random — every hit gets its own xorshift stream derived from the
 * pattern seed, track index and hit index, so re-ordering tracks or editing
 * one step never changes another hit.
 */

export interface Step { beat: number; sub: number; vel: number; note?: number }
/** One hit with its natural tail. `vel` (0..1) is for timbre; the sequencer applies it as level too. */
export type Instrument = (sr: number, vel: number, note: number, rng: () => number) => Float32Array
export interface Track { inst: Instrument; steps: Step[]; gain: number; note?: number }
/** `leadInS`: the loop point sits this far *before* beat 1 (default 0), so the seam never
 *  lands on a downbeat transient — both sides of the seam are then decaying tails (15 §4.2). */
export interface PatternOpts { sr: number; bars: number; bpm: number; seed: number; leadInS?: number }

const SUBS = 4

/** xorshift32 → [0, 1). Same generator family as `synth.ts`. */
export function makeRng(seed: number): () => number {
  let s = seed >>> 0
  if (s === 0) s = 0x9e3779b9
  return () => { s ^= s << 13; s ^= s >>> 17; s ^= s << 5; s >>>= 0; return s / 4294967296 }
}

/** FNV-1a over a string — stem seeds are `hashStr(id)`. */
export function hashStr(s: string): number { let h = 2166136261; for (let i = 0; i < s.length; i++) { h ^= s.charCodeAt(i); h = Math.imul(h, 16777619) } return h >>> 0 }

const mixSeed = (a: number, b: number, c: number): number => {
  let h = (a ^ Math.imul(b + 1, 0x85ebca6b) ^ Math.imul(c + 1, 0xc2b2ae35)) >>> 0
  h ^= h >>> 16; h = Math.imul(h, 0x7feb352d); h ^= h >>> 15; h = Math.imul(h, 0x846ca68b); h ^= h >>> 16
  return h >>> 0
}

/** Euclidean rhythm (Bjorklund distribution) — `euclid(3, 8)` is the tresillo `x..x..x.`. */
export function euclid(hits: number, steps: number): boolean[] {
  const out: boolean[] = []
  for (let i = 0; i < steps; i++) out.push(i === 0 ? hits > 0 : Math.floor((i * hits) / steps) !== Math.floor(((i - 1) * hits) / steps))
  return out
}

/**
 * Parse a 16th grid: 16 chars per bar, bars concatenated. `X` = accent (vel 1.0·vel),
 * `x` = normal (0.7·vel), `.` = rest; spaces and `|` are ignored.
 */
export function stepsFrom(grid: string, vel = 1): Step[] {
  const out: Step[] = []
  let k = 0
  for (const ch of grid) {
    if (ch === ' ' || ch === '|' || ch === '\n') continue
    if (ch === 'X' || ch === 'x') out.push({ beat: Math.floor(k / SUBS), sub: k % SUBS, vel: (ch === 'X' ? 1 : 0.7) * vel })
    k++
  }
  return out
}

/** Steps from a boolean mask (one entry per 16th), all at `vel`. */
export function stepsFromMask(mask: boolean[], vel = 1): Step[] {
  const out: Step[] = []
  mask.forEach((on, k) => { if (on) out.push({ beat: Math.floor(k / SUBS), sub: k % SUBS, vel }) })
  return out
}

/** Shift steps by whole beats (for placing a one-bar figure at bar `n`: `shift(steps, 4 * n)`). */
export function shift(steps: Step[], beats: number): Step[] { return steps.map((s) => ({ ...s, beat: s.beat + beats })) }

/**
 * Render exactly `bars · 4 · 60/bpm · sr` samples. A track whose steps span
 * fewer bars than the pattern repeats with that span as its period (a one-bar
 * grid plays every bar; an 8-bar grid plays once). Tails that run past the loop
 * end are folded onto the head (15 §4.2); the sum is clamped to ±1.
 */
export function renderPattern(tracks: Track[], o: PatternOpts): Float32Array {
  const beatLen = (60 / o.bpm) * o.sr
  const barLen = Math.round(4 * beatLen)
  const loopLen = Math.round(o.bars * 4 * beatLen)
  const lead = Math.round((o.leadInS ?? 0) * o.sr)
  const work = new Float32Array(loopLen + barLen)
  tracks.forEach((t, ti) => {
    if (!t.steps.length) return
    const maxBeat = Math.max(...t.steps.map((s) => s.beat))
    const periodBars = Math.max(1, Math.ceil((maxBeat + 1) / 4))
    let hitIndex = 0
    for (let rep = 0; rep * periodBars < o.bars; rep++) {
      for (const s of t.steps) {
        const beat = rep * periodBars * 4 + s.beat
        if (beat >= o.bars * 4) continue
        const at = lead + Math.round((beat + s.sub / SUBS) * beatLen)
        const rng = makeRng(mixSeed(o.seed, ti, hitIndex++))
        const hit = t.inst(o.sr, s.vel, s.note ?? t.note ?? 0, rng)
        const g = t.gain * s.vel
        const n = Math.min(hit.length, work.length - at)
        for (let i = 0; i < n; i++) work[at + i] += hit[i] * g
      }
    }
  })
  const out = new Float32Array(loopLen)
  for (let i = 0; i < loopLen; i++) {
    const v = i < barLen ? work[i] + work[i + loopLen] : work[i]
    out[i] = v > 1 ? 1 : v < -1 ? -1 : v
  }
  return out
}
